import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  fadeRise,
  maskRevealLine,
  ruleDraw,
  ruleDrawVertical,
  staggerChildren,
  viewportOnce,
  stagger as staggerTokens,
} from '../../lib/motion';

type RevealVariant = 'rise' | 'mask' | 'rule' | 'ruleVertical';

const VARIANTS = {
  rise: fadeRise,
  mask: maskRevealLine,
  rule: ruleDraw,
  ruleVertical: ruleDrawVertical,
} as const;

type RevealProps = {
  variant?: RevealVariant;
  /** Stagger children instead of animating this element itself. */
  staggerInterval?: number;
  delay?: number;
  className?: string;
  children?: React.ReactNode;
  as?: 'div' | 'section' | 'span' | 'h1' | 'h2' | 'h3' | 'p' | 'li' | 'ul';
} & Record<string, unknown>;

/**
 * Scroll-triggered reveal with the shared motion vocabulary.
 *
 * - `rise` (default): quiet fade-up for blocks of content.
 * - `mask`: line rises from below — wrap in an `overflow: hidden`
 *   parent (or use `MaskLine` below which brings its own).
 * - `rule` / `ruleVertical`: hairline rules drawing in. Set
 *   `transform-origin` via CSS (defaults: left / top).
 *
 * Honors prefers-reduced-motion by rendering static content.
 */
export const Reveal: React.FC<RevealProps> = ({
  variant = 'rise',
  staggerInterval,
  delay,
  className,
  children,
  as = 'div',
  ...rest
}) => {
  const reduced = useReducedMotion();
  const Component = motion[as];

  if (reduced) {
    const Tag = as;
    return (
      <Tag className={className} {...(rest as Record<string, never>)}>
        {children}
      </Tag>
    );
  }

  const variants = staggerInterval
    ? staggerChildren(staggerInterval)
    : VARIANTS[variant];

  return (
    <Component
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      variants={variants}
      transition={delay ? { delayChildren: delay } : undefined}
      {...rest}
    >
      {children}
    </Component>
  );
};

/** Child of a staggering `Reveal` — inherits the parent's timeline. */
export const RevealChild: React.FC<RevealProps> = ({
  variant = 'rise',
  className,
  children,
  as = 'div',
  ...rest
}) => {
  const reduced = useReducedMotion();
  const Component = motion[as];

  if (reduced) {
    const Tag = as;
    return (
      <Tag className={className} {...(rest as Record<string, never>)}>
        {children}
      </Tag>
    );
  }

  return (
    <Component className={className} variants={VARIANTS[variant]} {...rest}>
      {children}
    </Component>
  );
};

/**
 * A masked line of text: outer clips, inner rises. Use for masthead
 * and hero title lines.
 */
export const MaskLine: React.FC<{
  children: React.ReactNode;
  className?: string;
  delay?: number;
}> = ({ children, className, delay = 0 }) => {
  const reduced = useReducedMotion();

  if (reduced) {
    return <span className={className}>{children}</span>;
  }

  return (
    <span style={{ display: 'block', overflow: 'hidden' }}>
      <motion.span
        className={className}
        style={{ display: 'block' }}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={{
          hidden: maskRevealLine.hidden,
          visible: {
            ...(maskRevealLine.visible as object),
            transition: {
              ...((maskRevealLine.visible as { transition?: object }).transition ?? {}),
              delay,
            },
          },
        }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const STAGGER = staggerTokens;

export default Reveal;
